import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useQueryParams } from "@/hooks/useQueryParam";
import { useSlug } from "@/hooks/useSlug";
import { surveyMutations } from "@/queries/surveys/mutations";
import { useSurveyQueries } from "@/queries/surveys/queryOptions";
import { routes } from "@/routes";
import type { ApiQueryParams } from "@/types/api-types";

import type { SurveysRouteParams } from "../../routes";
import { SurveysList, type SurveysListProps } from "./SurveysList";

const SURVEYS_PER_PAGE = 15;

export function SurveysListPage(): React.ReactNode {
  const navigate = useNavigate();
  const slug = useSlug();
  const [routeParams, setRouteParams] = useQueryParams<SurveysRouteParams["list"]>();
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState(() => routeParams.search ?? "");
  const debouncedQuery = useDebounceValue(query.toLowerCase().trim());

  useEffect(() => {
    setRouteParams({ search: debouncedQuery || undefined }, { replace: true });
  }, [debouncedQuery, setRouteParams]);

  const queryParams: ApiQueryParams<"getSurveysV1"> = {
    Limit: SURVEYS_PER_PAGE,
    Offset: page * SURVEYS_PER_PAGE,
    search: debouncedQuery,
  };
  const surveyQueries = useSurveyQueries();
  const {
    data: surveys,
    error: surveysError,
    isFetching: isFetchingSurveys,
  } = useQuery({
    ...surveyQueries.list({ query: queryParams }),
    placeholderData: keepPreviousData,
    staleTime: 5000,
  });
  const deleteSurveyMutation = surveyMutations.useDeleteSurvey();

  const onSearch = useCallback((value: string) => {
    setQuery(value);
    setPage(0);
  }, []);

  const error = surveysError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const totalPages = surveys ? Math.ceil(surveys.total / SURVEYS_PER_PAGE) : 0;

  const contentProps: SurveysListProps = {
    surveys: surveys?.items ?? [],
    isLoading: isFetchingSurveys,
    page,
    totalPages,
    totalSurveys: surveys?.total,
    searchQuery: query,
    debouncedQuery: debouncedQuery,
    onPageChange: setPage,
    onSearch,
    onDelete: (surveyId) =>
      deleteSurveyMutation.mutateAsync(surveyId, {
        onSuccess: () => {
          void navigate(routes.surveys.list({ slug }));
        },
      }),
  };

  return <SurveysList {...contentProps} />;
}
